import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { MapPin, MessageCircle, Users, Crown, Phone, Mail } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getImageUrl } from '../utils/imageUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CHAPTER_DESCRIPTIONS = {
  "Durbanville": "Serving the Durbanville area and surrounds",
  "Brackenfell and Surrounds": "Connecting entrepreneurs in Brackenfell and nearby areas",
  "Helderberg": "The Helderberg region business community",
  "Milnerton": "Milnerton and surrounds business community",
  "Stellenbosch": "Wine country's vibrant business network",
  "Boland": "Serving the greater Boland region",
  "Southern Peninsula": "From Muizenberg to Simon's Town",
  "Rondebosch and Surrounds": "Southern suburbs business community",
  "West Coast Neighbours": "The beautiful West Coast region",
  "Jozi": "Johannesburg's mom entrepreneur community",
  "Randburg - Roodepoort": "Randburg and Roodepoort's mom entrepreneur community"
};

export default function ChaptersPage({ user }) {
  const [chapters, setChapters] = useState([]);
  const [chapterLeaders, setChapterLeaders] = useState([]);
  const [chapterSettings, setChapterSettings] = useState({});
  const [chapterAssistants, setChapterAssistants] = useState({});
  const [memberCounts, setMemberCounts] = useState({});
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [leadersRes, statsRes, usersRes, areasRes] = await Promise.all([
        axios.get(`${API}/chapters/leaders`),
        axios.get(`${API}/chapters/stats`),
        axios.get(`${API}/users`),
        axios.get(`${API}/areas`)
      ]);
      setChapterLeaders(leadersRes.data.leaders || []);
      setChapterSettings(leadersRes.data.chapter_settings || {});
      setChapterAssistants(leadersRes.data.assistants || {});
      
      // Build chapters from areas
      setChapters(areasRes.data.map(name => ({
        name,
        description: CHAPTER_DESCRIPTIONS[name] || `${name} business community`
      })));
      
      // Get admins
      const adminUsers = (usersRes.data || []).filter(u => u.is_admin);
      setAdmins(adminUsers);
      
      // Convert stats array to object for easy lookup
      const counts = {};
      statsRes.data.forEach(stat => {
        counts[stat.chapter] = stat.count;
      });
      setMemberCounts(counts);
    } catch (error) {
      console.error('Failed to load chapter data');
    } finally {
      setLoading(false);
    }
  };

  const getLeaderForChapter = (chapterName) => {
    return chapterLeaders.find(leader => 
      (leader.managed_areas && leader.managed_areas.includes(chapterName)) ||
      leader.managed_area === chapterName
    );
  };

  const getAssistantForChapter = (chapterName) => {
    const settings = chapterSettings[chapterName];
    if (settings && settings.assistant_leader_id) {
      return chapterAssistants[settings.assistant_leader_id] || null;
    }
    return null;
  };

  if (loading) return <div className="text-center py-12">Loading chapters...</div>;

  return (
    <div className="max-w-7xl mx-auto px-6" data-testid="chapters-page">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-[#2d3748] mb-4">
          Our Chapters
        </h1>
        <p className="text-lg text-[#64748b] max-w-2xl mx-auto">
          Mom's Biz Network is organized into local chapters across South Africa. 
          Find your nearest chapter and connect with women entrepreneurs in your area.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Admins Card */}
        {admins.length > 0 && (
          <Card className="border-[#cb9944]/30 rounded-2xl shadow-sm md:col-span-2 lg:col-span-3" data-testid="admins-card">
            <div className="bg-gradient-to-r from-[#cb9944] to-[#b08435] p-5 rounded-t-2xl">
              <div className="flex items-center gap-2 text-white">
                <Crown className="w-5 h-5" />
                <h2 className="text-lg font-bold">Admin Team</h2>
              </div>
            </div>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {admins.map((admin) => (
                  <div key={admin.id} className="flex items-center gap-3">
                    <div className="relative h-12 w-12 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
                      {admin.profile_image ? (
                        <img
                          src={getImageUrl(admin.profile_image)}
                          alt={admin.display_name || admin.full_name}
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center bg-[#cb9944] text-white font-semibold">
                          {(admin.display_name || admin.full_name || '?').charAt(0)}
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-[#2d3748] text-sm truncate">{admin.display_name || admin.full_name}</p>
                      {admin.business_name && (
                        <p className="text-xs text-[#5b7f95] truncate">{admin.business_name}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {chapters.map((chapter) => {
          const leader = getLeaderForChapter(chapter.name);
          const assistant = getAssistantForChapter(chapter.name);
          const memberCount = memberCounts[chapter.name] || 0;
          
          return (
            <Card key={chapter.name} className="border-gray-100 rounded-2xl shadow-sm hover:shadow-lg transition-all overflow-hidden" data-testid="chapter-card">
              {/* Chapter Header - Clickable */}
              <Link to={`/chapters/${encodeURIComponent(chapter.name)}`}>
                <div className="bg-gradient-to-r from-[#5b7f95] to-[#4a6b80] p-6 text-white cursor-pointer hover:from-[#4a6b80] hover:to-[#3d5a6b] transition-colors">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-5 h-5" />
                    <h2 className="text-xl font-bold">{chapter.name}</h2>
                  </div>
                  <p className="text-sm opacity-90">{chapter.description}</p>
                  <div className="flex items-center gap-1 mt-3 text-sm">
                    <Users className="w-4 h-4" />
                    <span>{memberCount} member{memberCount !== 1 ? 's' : ''}</span>
                  </div>
                </div>
              </Link>

              <CardContent className="p-6">
                {leader ? (
                  <div>
                    <p className="text-xs text-[#64748b] uppercase tracking-wide mb-3">Chapter Leader</p>
                    <div className="flex items-start gap-4">
                      <div className="relative h-16 w-16 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
                        {leader.profile_image ? (
                          <img 
                            src={getImageUrl(leader.profile_image)}
                            alt={leader.display_name || leader.full_name}
                            className="absolute inset-0 w-full h-full object-cover"
                          />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center bg-[#cb9944] text-white text-lg font-semibold">
                            {(leader.display_name || leader.full_name).charAt(0)}
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-[#2d3748]">
                          {leader.display_name || leader.full_name}
                        </h3>
                        {leader.business_name && (
                          <p className="text-sm text-[#5b7f95]">{leader.business_name}</p>
                        )}
                        {leader.business_description && (
                          <p className="text-sm text-[#64748b] mt-1 line-clamp-2">
                            {leader.business_description}
                          </p>
                        )}
                        {leader.phone && (
                          <p className="text-sm text-[#64748b] mt-1 flex items-center gap-1">
                            <Phone className="w-3.5 h-3.5 text-[#5b7f95]" />
                            <a href={`tel:${leader.phone}`} className="hover:text-[#5b7f95]">{leader.phone}</a>
                          </p>
                        )}
                        {(leader.display_email || leader.email) && (
                          <p className="text-sm text-[#64748b] mt-0.5 flex items-center gap-1 truncate">
                            <Mail className="w-3.5 h-3.5 text-[#5b7f95]" />
                            <a href={`mailto:${leader.display_email || leader.email}`} className="hover:text-[#5b7f95] truncate">{leader.display_email || leader.email}</a>
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Assistant Leader */}
                    {assistant && (
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <p className="text-xs text-[#cb9944] uppercase tracking-wide mb-2">Assistant Leader</p>
                        <div className="flex items-center gap-3">
                          <div className="relative h-10 w-10 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
                            {assistant.profile_image ? (
                              <img 
                                src={getImageUrl(assistant.profile_image)}
                                alt={assistant.display_name || assistant.full_name}
                                className="absolute inset-0 w-full h-full object-cover"
                              />
                            ) : (
                              <div className="absolute inset-0 flex items-center justify-center bg-[#cb9944]/70 text-white text-sm font-semibold">
                                {(assistant.display_name || assistant.full_name).charAt(0)}
                              </div>
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-sm text-[#2d3748]">{assistant.display_name || assistant.full_name}</p>
                            {assistant.business_name && (
                              <p className="text-xs text-[#5b7f95]">{assistant.business_name}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {user && leader.id !== user.id && (
                      <Link to={`/messages/${leader.id}`}>
                        <Button className="w-full mt-4 bg-[#cb9944] hover:bg-[#b08435] text-white rounded-full" data-testid="message-leader-btn">
                          <MessageCircle className="w-4 h-4 mr-2" />
                          Message {leader.display_name?.split(' ')[0] || leader.full_name?.split(' ')[0]}
                        </Button>
                      </Link>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-[#64748b] text-sm">No chapter leader assigned yet</p>
                    <p className="text-xs text-[#64748b] mt-1">Contact admin for more information</p>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
